import React, { PropsWithChildren, useEffect, useState } from 'react';
import { Tabs, Dropdown, MenuProps } from 'antd';
import { KeepAlive } from 'react-activation';
import type { TabsProps } from 'antd';
import { useTabContext } from '@/context/TabContext';
import { useNavigate, useLocation, useAppData } from '@umijs/max';
import Dashboard from '@/pages/Dashboard';

// Normalize path: convert '/' to '/dashboard', and lowercase everything
const normalizePath = (path: string): string => (path === '/' ? '/dashboard' : path.toLowerCase());

// Title fallback: use the last segment of the path and convert to Title Case
const formatTitle = (path: string): string => {
  const segments = path.split('/').filter(Boolean);
  const lastSegment = segments[segments.length - 1];
  return lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1);
};

const findRouteTitle = (path: string, routes: any[]): string | undefined => {
  for (const route of routes) {
    if (route.path === path) return route.name;
    if (route.routes) {
      const child = findRouteTitle(path, route.routes);
      if (child) return child;
    }
  }
  return undefined;
};

export default function SortableTabs({ children }: PropsWithChildren) {
  const { tabs, activeKey, addTab, removeTab, setActiveKey, setTabs } = useTabContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { clientRoutes } = useAppData();
  const [contextKey, setContextKey] = useState<string | null>(null);

  // Add dashboard tab once on mount
  useEffect(() => {
    if (!tabs.find((t) => t.key === '/dashboard')) {
      addTab({
        key: '/dashboard',
        title: 'Dashboard',
        content: <Dashboard />,
      });
    }
  }, []);

  // Add a new tab for the current route (if not already in tabs)
  useEffect(() => {
    const path = normalizePath(location.pathname);
    setActiveKey(path);
    if (!tabs.find((t) => t.key === path)) {
      const title = findRouteTitle(path, clientRoutes) || formatTitle(path);
      addTab({ key: path, title, content: children });
    }
  }, [location.pathname, children]);

  const handleChange = (key: string) => {
    setActiveKey(key);
    navigate(normalizePath(key));
  };

  const handleEdit: TabsProps['onEdit'] = (e, action) => {
    if (action === 'remove' && typeof e === 'string') {
      const index = tabs.findIndex((t) => t.key === e);
      const next = tabs[index + 1] || tabs[index - 1];
      removeTab(e);
      if (activeKey === e && next) {
        setActiveKey(next.key);
        navigate(normalizePath(next.key));
      } else if (!next) {
        setActiveKey('/dashboard');
        navigate('/dashboard');
      }
    }
  };

  const closeOthers = (key: string) => {
    setTabs(tabs.filter((tab) => tab.key === '/dashboard' || tab.key === key));
    setActiveKey(key);
    navigate(normalizePath(key));
  };

  const closeAll = () => {
    setTabs(tabs.filter((tab) => tab.key === '/dashboard'));
    setActiveKey('/dashboard');
    navigate('/dashboard');
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'closeOther',
      label: 'Close Others',
      onClick: () => contextKey && closeOthers(contextKey),
    },
    {
      key: 'closeAll',
      label: 'Close All',
      onClick: closeAll,
    },
  ];

  const tabItems = tabs.map((tab) => ({
    key: tab.key,
    label: (
      <Dropdown
        menu={{ items: menuItems }}
        trigger={['contextMenu']}
        onOpenChange={(visible) => visible && setContextKey(tab.key)}
      >
        <span title={tab.title}>{tab.title}</span>
      </Dropdown>
    ),
    closable: tab.key !== '/dashboard',
    children: (
      <KeepAlive id={tab.key} name={tab.title}>
        {tab.content}
      </KeepAlive>
    ),
  }));

  return (
    <Tabs
      hideAdd
      type="editable-card"
      activeKey={activeKey}
      onChange={handleChange}
      onEdit={handleEdit}
      items={tabItems}
    />
  );
}
